import { API_BASE } from "./base";
import {
  appendSuperAdminReportLocationFilters,
  withSuperAdminReportLocationFilters,
} from "../utils/superAdminReportLocationFilters";

const API_BASE_URL = API_BASE;

function getAuthHeaders() {
  const token = null;
  return {
    "Content-Type": "application/json",
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
}

async function parseJson(res) {
  const text = await res.text();
  if (!res.ok) {
    let payload = null;
    if (text) {
      try {
        payload = JSON.parse(text);
      } catch (err) {
        payload = null;
      }
    }
    const detail =
      (payload && typeof payload.detail === "string" && payload.detail) ||
      text ||
      `Request failed with status ${res.status}`;
    const error = new Error(detail);
    error.status = res.status;
    error.payload = payload;
    if (payload && typeof payload === "object" && payload.blockers) {
      error.blockers = payload.blockers;
    }
    throw error;
  }
  if (!text) return null;
  try {
    return JSON.parse(text);
  } catch (err) {
    return text;
  }
}

function addReportLocationQuery(query) {
  return appendSuperAdminReportLocationFilters(query);
}

function withReportLocationPayload(payload = {}) {
  return withSuperAdminReportLocationFilters(payload);
}

export async function fetchAllBusinesses({ light = false } = {}) {
  const params = new URLSearchParams();
  if (light) params.set("light", "1");
  const res = await fetch(`${API_BASE_URL}/superadmin/businesses${
    params.toString() ? `?${params.toString()}` : ""
  }`, {
    headers: getAuthHeaders(),
  });
  return parseJson(res);
}

export async function fetchAllSalons(business_id, { light = false } = {}) {
  const params = new URLSearchParams();
  if (business_id != null) {
    params.append("business_id", String(business_id));
  }
  if (light) params.set("light", "1");
  const url = `${API_BASE_URL}/superadmin/salons${
    params.toString() ? `?${params.toString()}` : ""
  }`;
  const res = await fetch(url, {
    headers: getAuthHeaders(),
  });
  return parseJson(res);
}

export async function fetchSalonById(salonId) {
  const res = await fetch(`${API_BASE_URL}/superadmin/salons/${salonId}`, {
    headers: getAuthHeaders(),
  });
  return parseJson(res);
}

export async function createSalon({ business_id, name, timezone }) {
  const res = await fetch(`${API_BASE_URL}/superadmin/salons`, {
    method: "POST",
    headers: getAuthHeaders(),
    body: JSON.stringify({
      business_id,
      name,
      timezone,
    }),
  });
  return parseJson(res);
}

export async function switchBusiness(business_id) {
  const res = await fetch(`${API_BASE_URL}/superadmin/switch-business`, {
    method: "POST",
    headers: getAuthHeaders(),
    body: JSON.stringify({ business_id }),
  });
  return parseJson(res);
}

export async function switchSalon(salon_id) {
  const res = await fetch(`${API_BASE_URL}/superadmin/switch-salon`, {
    method: "POST",
    headers: getAuthHeaders(),
    body: JSON.stringify({ salon_id }),
  });
  return parseJson(res);
}

export async function deleteEmptySalon(salon_id) {
  const res = await fetch(`${API_BASE_URL}/superadmin/salons/${salon_id}/empty`, {
    method: "DELETE",
    headers: getAuthHeaders(),
  });
  return parseJson(res);
}

export async function deleteEmptyBusiness(business_id) {
  const res = await fetch(`${API_BASE_URL}/superadmin/businesses/${business_id}/empty`, {
    method: "DELETE",
    headers: getAuthHeaders(),
  });
  return parseJson(res);
}

export async function fetchSuperAdminBillingRiskAlerts({
  status = "open",
  risk_level = "all",
  search = "",
  page = 1,
  page_size = 50,
} = {}) {
  const params = new URLSearchParams();
  if (status) params.append("status", status);
  if (risk_level) params.append("risk_level", risk_level);
  if (search) params.append("search", search);
  params.append("page", String(page || 1));
  params.append("page_size", String(page_size || 50));
  const res = await fetch(
    `${API_BASE_URL}/superadmin/billing-risk-alerts?${params.toString()}`,
    { headers: getAuthHeaders() }
  );
  return parseJson(res);
}

export async function updateSuperAdminBillingRiskAlert(alertId, payload) {
  const res = await fetch(`${API_BASE_URL}/superadmin/billing-risk-alerts/${alertId}/action`, {
    method: "POST",
    headers: getAuthHeaders(),
    body: JSON.stringify(payload || {}),
  });
  return parseJson(res);
}

export async function fetchSuperAdminBillingCoupons({
  status = "active",
  search = "",
  page = 1,
  page_size = 50,
} = {}) {
  const params = new URLSearchParams();
  if (status) params.append("status", status);
  if (search) params.append("search", search);
  params.append("page", String(page || 1));
  params.append("page_size", String(page_size || 50));
  const res = await fetch(
    `${API_BASE_URL}/superadmin/billing-coupons?${params.toString()}`,
    { headers: getAuthHeaders() }
  );
  return parseJson(res);
}

export async function createSuperAdminBillingCoupon(payload) {
  const res = await fetch(`${API_BASE_URL}/superadmin/billing-coupons`, {
    method: "POST",
    headers: getAuthHeaders(),
    body: JSON.stringify(payload || {}),
  });
  return parseJson(res);
}

export async function updateSuperAdminBillingCouponAction(couponId, payload) {
  const res = await fetch(`${API_BASE_URL}/superadmin/billing-coupons/${couponId}/action`, {
    method: "POST",
    headers: getAuthHeaders(),
    body: JSON.stringify(payload || {}),
  });
  return parseJson(res);
}

export async function fetchSuperAdminBillingCouponReport(filters = {}) {
  const params = addReportLocationQuery(new URLSearchParams());
  if (filters.start_date) params.set("start_date", String(filters.start_date));
  if (filters.end_date) params.set("end_date", String(filters.end_date));
  if (filters.status) params.set("status", String(filters.status));
  if (filters.search) params.set("search", String(filters.search));
  params.set("page", String(filters.page || 1));
  params.set("page_size", String(filters.page_size || 50));
  const res = await fetch(
    `${API_BASE_URL}/superadmin/reports/billing-coupons?${params.toString()}`,
    { headers: getAuthHeaders() }
  );
  return parseJson(res);
}

export async function fetchAllUsers({ q, salon_id, business_id, page, page_size } = {}) {
  const params = new URLSearchParams();
  if (q) params.append("q", String(q));
  if (salon_id != null && salon_id !== "") {
    params.append("salon_id", String(salon_id));
  }
  if (business_id != null && business_id !== "") {
    params.append("business_id", String(business_id));
  }
  if (page != null && page !== "") {
    params.append("page", String(page));
  }
  if (page_size != null && page_size !== "") {
    params.append("page_size", String(page_size));
  }
  const url = `${API_BASE_URL}/superadmin/users${
    params.toString() ? `?${params.toString()}` : ""
  }`;
  const res = await fetch(url, {
    headers: getAuthHeaders(),
  });
  return parseJson(res);
}

export async function fetchSuperAdminClients(params = {}) {
  const query = new URLSearchParams();
  if (params.search) query.set("search", String(params.search));
  if (params.business_id != null && params.business_id !== "") {
    query.set("business_id", String(params.business_id));
  }
  if (params.salon_id != null && params.salon_id !== "") {
    query.set("salon_id", String(params.salon_id));
  }
  if (params.limit != null && params.limit !== "") {
    query.set("limit", String(params.limit));
  }
  if (params.require_query != null) {
    query.set("require_query", params.require_query ? "true" : "false");
  }
  if (params.exclude_house != null) {
    query.set("exclude_house", params.exclude_house ? "true" : "false");
  }
  const url = `${API_BASE_URL}/superadmin/clients${
    query.toString() ? `?${query.toString()}` : ""
  }`;
  const res = await fetch(url, {
    headers: getAuthHeaders(),
  });
  return parseJson(res);
}

export async function assignSuperAdminClientSalon(client_id, salon_id) {
  const res = await fetch(`${API_BASE_URL}/superadmin/clients/${client_id}/assign-salon`, {
    method: "PATCH",
    headers: getAuthHeaders(),
    body: JSON.stringify({ salon_id }),
  });
  return parseJson(res);
}

export async function fetchSalonUsers(salon_id) {
  const res = await fetch(`${API_BASE_URL}/superadmin/salons/${salon_id}/users`, {
    headers: getAuthHeaders(),
  });
  return parseJson(res);
}

export async function resetUserPassword(user_id, { new_password, force_change }) {
  const res = await fetch(`${API_BASE_URL}/superadmin/users/${user_id}/reset-password`, {
    method: "POST",
    headers: getAuthHeaders(),
    body: JSON.stringify({ new_password, force_change }),
  });
  return parseJson(res);
}

function appendRegistrationParams(params, filters = {}) {
  Object.entries(filters || {}).forEach(([key, value]) => {
    if (value === undefined || value === null || value === "") return;
    params.set(key, String(value));
  });
  return params;
}

function appendLoginActivityParams(params, filters = {}) {
  Object.entries(filters || {}).forEach(([key, value]) => {
    if (value === undefined || value === null || value === "") return;
    params.set(key, String(value));
  });
  return params;
}

export async function fetchSuperAdminRegistrationActivity(filters = {}) {
  const params = appendRegistrationParams(new URLSearchParams(), filters);
  const url = `${API_BASE_URL}/superadmin/registration-activity${
    params.toString() ? `?${params.toString()}` : ""
  }`;
  const res = await fetch(url, { headers: getAuthHeaders() });
  return parseJson(res);
}

export function buildSuperAdminRegistrationActivityExportUrl(filters = {}) {
  const params = appendRegistrationParams(new URLSearchParams(), filters);
  return `${API_BASE_URL}/superadmin/registration-activity/export${
    params.toString() ? `?${params.toString()}` : ""
  }`;
}

export async function fetchSuperAdminRegistrationAnalytics(filters = {}) {
  const params = appendSuperAdminReportLocationFilters(
    new URLSearchParams()
  );
  appendRegistrationParams(params, filters);
  const url = `${API_BASE_URL}/superadmin/reports/registration-analytics${
    params.toString() ? `?${params.toString()}` : ""
  }`;
  const res = await fetch(url, { headers: getAuthHeaders() });
  return parseJson(res);
}

export async function fetchSuperAdminLoginActivity(filters = {}) {
  const params = appendLoginActivityParams(new URLSearchParams(), filters);
  const url = `${API_BASE_URL}/superadmin/login-activity${
    params.toString() ? `?${params.toString()}` : ""
  }`;
  const res = await fetch(url, { headers: getAuthHeaders() });
  return parseJson(res);
}

export function buildSuperAdminLoginActivityExportUrl(filters = {}) {
  const params = appendLoginActivityParams(new URLSearchParams(), filters);
  return `${API_BASE_URL}/superadmin/login-activity/export${
    params.toString() ? `?${params.toString()}` : ""
  }`;
}

export async function fetchSuperAdminLoginAnalytics(filters = {}) {
  const params = appendSuperAdminReportLocationFilters(new URLSearchParams());
  appendLoginActivityParams(params, filters);
  const url = `${API_BASE_URL}/superadmin/reports/login-analytics${
    params.toString() ? `?${params.toString()}` : ""
  }`;
  const res = await fetch(url, { headers: getAuthHeaders() });
  return parseJson(res);
}

export async function fetchSuperAdminMarketplaceOverview() {
  const res = await fetch(`${API_BASE_URL}/superadmin/marketplace/overview`, {
    headers: getAuthHeaders(),
  });
  return parseJson(res);
}

export async function fetchSuperAdminMarketplaceBooths(params = {}) {
  const query = new URLSearchParams();
  if (params.business_id != null && params.business_id !== "") {
    query.set("business_id", String(params.business_id));
  }
  if (params.salon_id != null && params.salon_id !== "") {
    query.set("salon_id", String(params.salon_id));
  }
  if (params.q) {
    query.set("q", String(params.q));
  }
  if (params.availability) {
    query.set("availability", String(params.availability));
  }
  if (params.limit != null && params.limit !== "") {
    query.set("limit", String(params.limit));
  }
  const res = await fetch(
    `${API_BASE_URL}/superadmin/marketplace/booths${
      query.toString() ? `?${query.toString()}` : ""
    }`,
    {
      headers: getAuthHeaders(),
    }
  );
  return parseJson(res);
}

export async function fetchSuperAdminMarketplaceBoothById(boothId) {
  const res = await fetch(`${API_BASE_URL}/superadmin/marketplace/booths/${boothId}`, {
    headers: getAuthHeaders(),
  });
  return parseJson(res);
}

export async function updateSuperAdminMarketplaceBoothAvailability(
  boothId,
  { is_available, reason }
) {
  const res = await fetch(
    `${API_BASE_URL}/superadmin/marketplace/booths/${boothId}/availability`,
    {
      method: "PATCH",
      headers: getAuthHeaders(),
      body: JSON.stringify({ is_available, reason }),
    }
  );
  return parseJson(res);
}

export async function fetchSuperAdminMarketplaceReadiness(params = {}) {
  const query = new URLSearchParams();
  if (params.business_id != null && params.business_id !== "") {
    query.set("business_id", String(params.business_id));
  }
  if (params.salon_id != null && params.salon_id !== "") {
    query.set("salon_id", String(params.salon_id));
  }
  if (params.only_not_ready != null) {
    query.set("only_not_ready", params.only_not_ready ? "true" : "false");
  }
  const res = await fetch(
    `${API_BASE_URL}/superadmin/marketplace/readiness${
      query.toString() ? `?${query.toString()}` : ""
    }`,
    {
      headers: getAuthHeaders(),
    }
  );
  return parseJson(res);
}

export async function fetchSuperAdminMarketplaceOwners(params = {}) {
  const query = new URLSearchParams();
  if (params.business_id != null && params.business_id !== "") {
    query.set("business_id", String(params.business_id));
  }
  if (params.salon_id != null && params.salon_id !== "") {
    query.set("salon_id", String(params.salon_id));
  }
  if (params.q) {
    query.set("q", String(params.q));
  }
  const res = await fetch(
    `${API_BASE_URL}/superadmin/marketplace/owners${
      query.toString() ? `?${query.toString()}` : ""
    }`,
    {
      headers: getAuthHeaders(),
    }
  );
  return parseJson(res);
}

export async function fetchSuperAdminMarketplaceSalonsWithoutBooths() {
  const res = await fetch(`${API_BASE_URL}/superadmin/marketplace/salons-without-booths`, {
    headers: getAuthHeaders(),
  });
  return parseJson(res);
}

export async function fetchSuperAdminReportFilters() {
  const query = addReportLocationQuery(new URLSearchParams());
  const res = await fetch(
    `${API_BASE_URL}/superadmin/reports/filters${
      query.toString() ? `?${query.toString()}` : ""
    }`,
    {
      headers: getAuthHeaders(),
    }
  );
  return parseJson(res);
}

export async function fetchSuperAdminReportKpis(params = {}) {
  const query = addReportLocationQuery(new URLSearchParams());
  if (params.scope) query.set("scope", String(params.scope));
  if (params.business_id != null && params.business_id !== "") {
    query.set("business_id", String(params.business_id));
  }
  if (params.salon_id != null && params.salon_id !== "") {
    query.set("salon_id", String(params.salon_id));
  }
  if (params.preset) query.set("preset", String(params.preset));
  if (params.start_date) query.set("start_date", String(params.start_date));
  if (params.end_date) query.set("end_date", String(params.end_date));
  if (params.group_by) query.set("group_by", String(params.group_by));
  query.set("include_refunds", params.include_refunds === false ? "false" : "true");
  query.set("include_voided", params.include_voided ? "true" : "false");
  if (Array.isArray(params.ticket_status)) {
    params.ticket_status.forEach((status) => {
      if (status != null && String(status).trim()) {
        query.append("ticket_status", String(status).trim());
      }
    });
  }

  const url = `${API_BASE_URL}/superadmin/reports/kpis${
    query.toString() ? `?${query.toString()}` : ""
  }`;
  const res = await fetch(url, {
    headers: getAuthHeaders(),
  });
  return parseJson(res);
}

export async function fetchSuperAdminSalesByEmployeeFilters() {
  const query = addReportLocationQuery(new URLSearchParams());
  const res = await fetch(
    `${API_BASE_URL}/superadmin/reports/sales-by-employee/filters${
      query.toString() ? `?${query.toString()}` : ""
    }`,
    {
      headers: getAuthHeaders(),
    }
  );
  return parseJson(res);
}

export async function fetchSuperAdminSalesByEmployee(payload = {}) {
  const res = await fetch(`${API_BASE_URL}/superadmin/reports/sales-by-employee`, {
    method: "POST",
    headers: getAuthHeaders(),
    body: JSON.stringify(withReportLocationPayload(payload)),
  });
  return parseJson(res);
}

export async function exportSuperAdminSalesByEmployee(payload = {}) {
  const res = await fetch(
    `${API_BASE_URL}/superadmin/reports/sales-by-employee/export`,
    {
      method: "POST",
      headers: getAuthHeaders(),
      body: JSON.stringify(withReportLocationPayload(payload)),
    }
  );
  if (!res.ok) {
    await parseJson(res);
  }
  return res.blob();
}

export async function getSuperAdminTaxLiabilityFilters(params = {}) {
  const query = addReportLocationQuery(new URLSearchParams());
  if (params.scope) query.set("scope", String(params.scope));
  if (params.business_id != null && params.business_id !== "") {
    query.set("business_id", String(params.business_id));
  }
  if (params.salon_id != null && params.salon_id !== "") {
    query.set("salon_id", String(params.salon_id));
  }
  const url = `${API_BASE_URL}/superadmin/reports/tax-liability/filters${
    query.toString() ? `?${query.toString()}` : ""
  }`;
  const res = await fetch(url, {
    headers: getAuthHeaders(),
  });
  return parseJson(res);
}

export async function postSuperAdminTaxLiabilitySummary(payload = {}) {
  const res = await fetch(`${API_BASE_URL}/superadmin/reports/tax-liability/summary`, {
    method: "POST",
    headers: getAuthHeaders(),
    body: JSON.stringify(withReportLocationPayload(payload)),
  });
  return parseJson(res);
}

export async function postSuperAdminTaxLiabilityDetail(payload = {}) {
  const res = await fetch(`${API_BASE_URL}/superadmin/reports/tax-liability/detail`, {
    method: "POST",
    headers: getAuthHeaders(),
    body: JSON.stringify(withReportLocationPayload(payload)),
  });
  return parseJson(res);
}

export async function exportSuperAdminTaxLiabilitySummary(payload = {}) {
  const res = await fetch(
    `${API_BASE_URL}/superadmin/reports/tax-liability/summary/export`,
    {
      method: "POST",
      headers: getAuthHeaders(),
      body: JSON.stringify(withReportLocationPayload(payload)),
    }
  );
  if (!res.ok) {
    await parseJson(res);
  }
  return res.blob();
}

export async function exportSuperAdminTaxLiabilityDetail(payload = {}) {
  const res = await fetch(
    `${API_BASE_URL}/superadmin/reports/tax-liability/detail/export`,
    {
      method: "POST",
      headers: getAuthHeaders(),
      body: JSON.stringify(withReportLocationPayload(payload)),
    }
  );
  if (!res.ok) {
    await parseJson(res);
  }
  return res.blob();
}

export async function getSuperAdminServiceAnalyticsFilters(params = {}) {
  const query = addReportLocationQuery(new URLSearchParams());
  if (params.scope) query.set("scope", String(params.scope));
  if (params.business_id != null && params.business_id !== "") {
    query.set("business_id", String(params.business_id));
  }
  if (params.salon_id != null && params.salon_id !== "") {
    query.set("salon_id", String(params.salon_id));
  }
  const url = `${API_BASE_URL}/superadmin/reports/service-analytics/filters${
    query.toString() ? `?${query.toString()}` : ""
  }`;
  const res = await fetch(url, {
    headers: getAuthHeaders(),
  });
  return parseJson(res);
}

export async function postSuperAdminServiceAnalytics(payload = {}) {
  const res = await fetch(`${API_BASE_URL}/superadmin/reports/service-analytics`, {
    method: "POST",
    headers: getAuthHeaders(),
    body: JSON.stringify(withReportLocationPayload(payload)),
  });
  return parseJson(res);
}

export async function getSuperAdminTotalSheetFilters(params = {}) {
  const query = addReportLocationQuery(new URLSearchParams());
  if (params.scope) query.set("scope", String(params.scope));
  if (params.business_id != null && params.business_id !== "") {
    query.set("business_id", String(params.business_id));
  }
  if (params.salon_id != null && params.salon_id !== "") {
    query.set("salon_id", String(params.salon_id));
  }
  const url = `${API_BASE_URL}/superadmin/reports/total-sheet/filters${
    query.toString() ? `?${query.toString()}` : ""
  }`;
  const res = await fetch(url, {
    headers: getAuthHeaders(),
  });
  return parseJson(res);
}

export async function postSuperAdminTotalSheetSummary(payload = {}) {
  const res = await fetch(`${API_BASE_URL}/superadmin/reports/total-sheet/summary`, {
    method: "POST",
    headers: getAuthHeaders(),
    body: JSON.stringify(withReportLocationPayload(payload)),
  });
  return parseJson(res);
}

export async function postSuperAdminTotalSheetDetail(payload = {}) {
  const res = await fetch(`${API_BASE_URL}/superadmin/reports/total-sheet/detail`, {
    method: "POST",
    headers: getAuthHeaders(),
    body: JSON.stringify(withReportLocationPayload(payload)),
  });
  return parseJson(res);
}

export async function exportSuperAdminTotalSheetPdf(payload = {}) {
  const res = await fetch(`${API_BASE_URL}/superadmin/reports/total-sheet/export/pdf`, {
    method: "POST",
    headers: getAuthHeaders(),
    body: JSON.stringify(withReportLocationPayload(payload)),
  });
  if (!res.ok) {
    await parseJson(res);
  }
  return res.blob();
}

export async function exportSuperAdminTotalSheetDetail(payload = {}) {
  const res = await fetch(
    `${API_BASE_URL}/superadmin/reports/total-sheet/export/detail`,
    {
      method: "POST",
      headers: getAuthHeaders(),
      body: JSON.stringify(withReportLocationPayload(payload)),
    }
  );
  if (!res.ok) {
    await parseJson(res);
  }
  return res.blob();
}

export async function getSuperAdminAppointmentStatusFilters(params = {}) {
  const query = addReportLocationQuery(new URLSearchParams());
  if (params.scope) query.set("scope", String(params.scope));
  if (params.business_id != null && params.business_id !== "") {
    query.set("business_id", String(params.business_id));
  }
  if (params.salon_id != null && params.salon_id !== "") {
    query.set("salon_id", String(params.salon_id));
  }
  const url = `${API_BASE_URL}/superadmin/reports/appointments-status/filters${
    query.toString() ? `?${query.toString()}` : ""
  }`;
  const res = await fetch(url, {
    headers: getAuthHeaders(),
  });
  return parseJson(res);
}

export async function postSuperAdminAppointmentStatusReport(payload = {}) {
  const res = await fetch(`${API_BASE_URL}/superadmin/reports/appointments-status`, {
    method: "POST",
    headers: getAuthHeaders(),
    body: JSON.stringify(withReportLocationPayload(payload)),
  });
  return parseJson(res);
}

export async function getSuperAdminCategoryAnalyticsFilters(params = {}) {
  const query = addReportLocationQuery(new URLSearchParams());
  if (params.scope) query.set("scope", String(params.scope));
  if (params.business_id != null && params.business_id !== "") {
    query.set("business_id", String(params.business_id));
  }
  if (params.salon_id != null && params.salon_id !== "") {
    query.set("salon_id", String(params.salon_id));
  }
  const url = `${API_BASE_URL}/superadmin/reports/category-analytics/filters${
    query.toString() ? `?${query.toString()}` : ""
  }`;
  const res = await fetch(url, {
    headers: getAuthHeaders(),
  });
  return parseJson(res);
}

export async function postSuperAdminCategoryAnalytics(payload = {}) {
  const res = await fetch(`${API_BASE_URL}/superadmin/reports/category-analytics`, {
    method: "POST",
    headers: getAuthHeaders(),
    body: JSON.stringify(withReportLocationPayload(payload)),
  });
  return parseJson(res);
}
